import PractitionerFilter from "./calendar/PractitionerFilter";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OrganizationSelect from "./OrganizationSelect";
import SupplierAvailabilityModal from "./SupplierAvailabilityModal";
import moment from "moment-timezone";
import { Alert, Button, CircularProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useSchedulingOrg } from "../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { loadPractitionerName, displayPractitionerName } from "./names";
import { buildCalendarEvents } from "./calendar/eventModel";
import CalendarFilters from "./calendar/CalendarFilters";
import OverviewMultiSelect from "./calendar/OverviewMultiSelect";
import ProviderDayBoard from "./calendar/ProviderDayBoard";
import ProviderWeekBoard from "./calendar/ProviderWeekBoard";
import useOverviewPage, { fetchOverview } from "./calendar/useOverviewPage";
import { useQueryClient } from "react-query";
import { schedulingQuery } from "../../../lib/schedulingCache";
import AppointmentMoveModal from "./calendar/AppointmentMoveModal";
import AppointmentDetailModal from "./appointments/AppointmentDetailModal";
import WorkingHoursDetailModal from "./WorkingHoursDetailModal";
import CalendarBlockDetailModal from "./CalendarBlockDetailModal";
import TimeZoneSelect from "./TimeZoneSelect";
import { useSchedulingTimeZone } from "../../../hooks/useSchedulingTimeZone";
import "./Scheduling.css";

const ScheduleOverview = () => {
  const navigate = useNavigate();
  const orgContext = useSchedulingOrg();
  const { excludedOrganizationRefs, organizationRef, organizationTimeZone, user } = orgContext;
  const [supplier, setSupplier] = useState(null);
  const displayZone = useSchedulingTimeZone(user);
  const [view, setView] = useState("day");
  const [date, setDate] = useState(() => moment.tz(displayZone.timeZone).format("YYYY-MM-DD"));
  const rangeStart = moment.tz(date, displayZone.timeZone).startOf(view === "week" ? "isoWeek" : "day");
  const range = { startsAt: rangeStart.toISOString(), endsAt: rangeStart.clone().add(view === "week" ? 7 : 1, "days").toISOString() };
  const [practitionerFilter, setPractitionerFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [workingToday, setWorkingToday] = useState(false);
  useEffect(() => {
    setPractitionerFilter([]);
    setCategoryFilter([]);
    setColumnOrder([]);
    setWorkingToday(false);
  }, [organizationRef, excludedOrganizationRefs]);
  const showBookings = true;
  const [eventFilter, setEventFilter] = useState("all");
  const [slotMinutes, setSlotMinutes] = useState(20);
  const [practitionerNames, setPractitionerNames] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [appointmentMove, setAppointmentMove] = useState(null);
  useEffect(() => { setAppointmentMove(null); setSelectedEvent(null); setSupplier(null); }, [organizationRef, excludedOrganizationRefs]);

  const queryClient = useQueryClient();
  const scope = JSON.stringify([organizationRef, excludedOrganizationRefs, view, range.startsAt, range.endsAt, practitionerFilter, categoryFilter, displayZone.timeZone, workingToday]);
  const [paging, setPaging] = useState({ scope: "", cursors: [undefined], index: 0 });
  const page = paging.scope === scope ? paging : { scope, cursors: [undefined], index: 0 };
  const overviewQuery = useOverviewPage({ excludedOrganizationRefs, organizationRef, startsAt: range.startsAt, endsAt: range.endsAt,
    timezoneName: displayZone.timeZone, practitionerRefs: practitionerFilter, serviceCategoryIds: categoryFilter,
    overview: view === "day" ? "details" : "summary", limit: 10, cursor: page.cursors[page.index],
    working: view === "day" && workingToday ? true : undefined });
  const schedules = overviewQuery.data?.schedules || [];
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "own", excludedOrganizationRefs, organizationRef, active: undefined });
  const availability = overviewQuery.data?.availability || [];
  const blocks = overviewQuery.data?.blocks || [];
  const appointments = overviewQuery.data?.appointments || [];
  const loadDay = useCallback((ref, date, signal) => {
    const day = moment.tz(date, displayZone.timeZone).startOf("day");
    const params = { excludedOrganizationRefs, organizationRef, startsAt: day.toISOString(), endsAt: day.clone().add(1, "day").toISOString(),
      timezoneName: displayZone.timeZone, practitionerRefs: [ref], serviceCategoryIds: categoryFilter, overview: "details", limit: 1 };
    return queryClient.fetchQuery(["scheduling-overview-page", params], schedulingQuery(({ consistency }) => fetchOverview(params, signal, consistency)), { staleTime: 30000, retry: false });
  }, [organizationRef, excludedOrganizationRefs, displayZone.timeZone, categoryFilter, queryClient]);

  const practitionerRefsKey = JSON.stringify([...new Map(schedules.map(schedule => [schedule.practitionerRef, {
    ref: schedule.practitionerRef, name: schedule.practitionerName, supplyOnly: schedule.supplyOnly,
  }])).values()].sort((a, b) => (a.ref || "").localeCompare(b.ref || "")));
  useEffect(() => {
    let cancelled = false;
    setPractitionerNames({});
    const refs = JSON.parse(practitionerRefsKey);
    (async () => {
      // Resolve every scheduled person, including those beyond the user-list page.
      for (let offset = 0; offset < refs.length; offset += 10) {
        const entries = await Promise.all(refs.slice(offset, offset + 10).map(async ({ ref, name: suppliedName, supplyOnly }) => {
          const name = suppliedName ? displayPractitionerName({ name: suppliedName }) : supplyOnly ? "Practitioner" : await loadPractitionerName(ref);
          return [ref, name && name !== ref && name !== "Unknown" ? name : "Practitioner"];
        }));
        if (cancelled) return;
        setPractitionerNames((current) => ({ ...current, ...Object.fromEntries(entries) }));
      }
    })();
    return () => { cancelled = true; };
  }, [organizationRef, practitionerRefsKey]);

  const scheduleById = useMemo(() => {
    const map = {};
    schedules.forEach((schedule) => {
      map[schedule.scheduleId] = schedule;
    });
    return map;
  }, [schedules]);

  const namesByRef = useMemo(() => {
    const map = { ...practitionerNames };
    schedules.forEach((schedule) => {
      map[schedule.scheduleId] = practitionerNames[schedule.practitionerRef] || "Loading practitioner…";
    });
    return map;
  }, [practitionerNames, schedules]);

  const overviewZone = displayZone.timeZone;
  const timeZoneControl = <TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
    localZone={displayZone.deviceTimeZone} organizationZone={organizationTimeZone} />;

  const filteredAvailability = availability.filter((window) => {
    const schedule = scheduleById[window.scheduleId];
    if (practitionerFilter.length && !practitionerFilter.includes(schedule?.practitionerRef)) return false;
    if (categoryFilter.length && !categoryFilter.some((id) => (window.serviceCategoryIds || []).includes(id))) return false;
    return true;
  });
  const filteredBlocks = blocks.filter((block) => {
    if (!practitionerFilter.length) return true;
    return practitionerFilter.includes(block.practitionerRef);
  });
  const filteredAppointments = appointments.filter((appointment) => {
    if (practitionerFilter.length && !practitionerFilter.some((ref) => (appointment.practitionerRefs || []).includes(ref))) {
      return false;
    }
    if (categoryFilter.length && !categoryFilter.includes(appointment.serviceCategoryId)) return false;
    return true;
  });

  const events = useMemo(
    () =>
      buildCalendarEvents({
        availability: filteredAvailability,
        blocks: filteredBlocks,
        appointments: filteredAppointments,
        categories,
        practitionerNames: namesByRef,
        showBookings,
        titlePrefix: true,
      }),
    [filteredAvailability, filteredBlocks, filteredAppointments, categories, namesByRef, showBookings]
  );

  const practitionerOptions = [
    ...Object.entries(practitionerNames).map(([value, label]) => {
      const parent = schedules.find(schedule => schedule.practitionerRef === value && schedule.supplyOnly);
      return { value, label: `${label}${parent ? ` · Parent · ${parent.organizationName || ""}` : ""}` };
    }),
  ];
  const categoryOptions = [
    ...categories.map((category) => ({ value: category.serviceCategoryId, label: `${category.name}${category.organizationRef ? ` · ${category.organizationName || category.organizationRef}` : ""}` })),
  ];

  const openPractitioner = (_event, props) => {
          if (_event) {
            if (props.source?.supplyOnly && props.kind === "block") return;
            setSelectedEvent(props);
            return;
          }
          const source = props.source;
          const practitionerId =
            props.practitionerRef ||
            source.practitionerRef ||
            source.practitionerRefs?.[0] ||
            scheduleById[source.scheduleId]?.practitionerRef;
          const target = schedules.find(schedule => schedule.practitionerRef === practitionerId && schedule.scheduleId === source.scheduleId) || schedules.find(schedule => schedule.practitionerRef === practitionerId);
          if (!practitionerId || !target) return;
          if (target.supplyOnly && !target.canManage) { setSupplier(target); return; }
          navigate("/app/schedule", {
            state: {
              organizationRef: target.organizationRef,
              scheduleId: target.scheduleId,
              data: {
                resourceType: "Practitioner",
                id: practitionerId,
                name: [{ text: practitionerNames[practitionerId] || "Practitioner" }],
              },
            },
          });
  };
  const visibleEvents = events.filter((event) => eventFilter === "all" || event.extendedProps.kind === eventFilter);
  const filteredSchedules = schedules.filter((schedule) =>
    (!practitionerFilter.length || practitionerFilter.includes(schedule.practitionerRef)) &&
    (!categoryFilter.length || categoryFilter.some((id) => schedule.serviceCategoryIds?.includes(id))));
  const openDay = (nextDate) => { setDate(nextDate); setView("day"); };

  return (
    <section className="common-listing scheduling-workspace">
      <div className="scheduling-manage-heading schedule-overview-heading">
        <div><span className="scheduling-ui-kicker">Team overview</span><h1>Team schedule</h1></div>
        <ToggleButtonGroup value={view} exclusive onChange={(_, next) => { if (next) setView(next); }} size="small" aria-label="Schedule view">
          <ToggleButton value="day">Day</ToggleButton><ToggleButton value="week">Week</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className="schedule-overview-filters">
        <OrganizationSelect context={orgContext} inputId="overview-org" />
        <div><label htmlFor="overview-category">Appointment type</label><OverviewMultiSelect inputId="overview-category" options={categoryOptions}
          value={categoryFilter} onChange={setCategoryFilter} allLabel="All appointment types" placeholder="Search appointment types…" /></div>
        <div><label htmlFor="overview-practitioner">Practitioner</label><PractitionerFilter organizationRef={organizationRef} options={practitionerOptions}
          value={practitionerFilter} onChange={setPractitionerFilter} allLabel="All practitioners" placeholder="Search practitioners…" /></div>
      </div>
      <CalendarFilters filter={eventFilter} onFilterChange={setEventFilter}
        {...(view === "day" ? { slotMinutes, onSlotMinutesChange: setSlotMinutes, workingToday,
          onWorkingTodayChange: (next) => { setWorkingToday(next); setPractitionerFilter([]); } } : {})} />
      {view === "day" ? overviewQuery.isError ? <Alert severity="error" action={<Button onClick={() => overviewQuery.refetch()}>Retry</Button>}>{overviewQuery.error.message || "Could not load this day."}</Alert> : overviewQuery.isLoading ?
        <div className="week-loading" role="status"><CircularProgress size={24} /> Loading day schedule…</div> : <ProviderDayBoard date={date} onDateChange={setDate} categories={categories} availabilityWindows={availability} schedules={filteredSchedules} names={practitionerNames} events={visibleEvents}
          timeZone={overviewZone} timeZoneControl={timeZoneControl} onEventClick={openPractitioner} onAppointmentMove={setAppointmentMove} slotMinutes={slotMinutes} columnOrder={columnOrder} onColumnOrderChange={setColumnOrder} />
        : <ProviderWeekBoard date={date} onDateChange={setDate} schedules={filteredSchedules} names={practitionerNames} events={events}
        timeZone={overviewZone} timeZoneControl={timeZoneControl} categories={categories} onEventClick={openPractitioner} onOpenDay={openDay}
        loading={overviewQuery.isLoading} summaryData={overviewQuery.data} loadDay={loadDay} eventFilter={eventFilter}
        error={overviewQuery.isError} onRetry={() => overviewQuery.refetch()}
        filterActive={!!(categoryFilter.length || practitionerFilter.length)} />}
      <div className="schedule-page-controls" aria-label="Practitioner pages">
        <span aria-live="polite">{overviewQuery.isError ? "Results unavailable" : overviewQuery.isLoading ? "Loading practitioners…" : `Page ${page.index + 1} · ${overviewQuery.data?.practitioners.length || 0} practitioners shown`}</span>
        <Button disabled={overviewQuery.isFetching} onClick={() => overviewQuery.refetch()}>{overviewQuery.isFetching && !overviewQuery.isLoading ? "Refreshing…" : "Refresh"}</Button>
        <Button disabled={page.index === 0 || overviewQuery.isFetching} onClick={() => setPaging({ ...page, index: page.index - 1 })}>Previous</Button>
        <Button disabled={!overviewQuery.data?.nextCursor || overviewQuery.isFetching} onClick={() => setPaging({ scope, cursors: [...page.cursors.slice(0, page.index + 1), overviewQuery.data.nextCursor], index: page.index + 1 })}>Next practitioners</Button>
      </div>
      {appointmentMove && <AppointmentMoveModal move={appointmentMove} organizationRef={organizationRef} names={practitionerNames} timeZone={overviewZone}
        onHide={() => setAppointmentMove(null)} onChanged={() => { setAppointmentMove(null); }} />}
      {selectedEvent?.kind === "appointment" && <AppointmentDetailModal
        appointment={selectedEvent.source} organizationRef={organizationRef} timezoneName={overviewZone}
        scheduleTimezoneName={scheduleById[selectedEvent.source.scheduleIds?.[0]]?.timezoneName}
        onHide={() => setSelectedEvent(null)}
        onChanged={() => { setSelectedEvent(null); }} />}
      {selectedEvent?.kind === "availability" && <WorkingHoursDetailModal
        window={selectedEvent.source} categories={categories} timezoneName={overviewZone}
        canEdit={false} onHide={() => setSelectedEvent(null)} />}
      {selectedEvent?.kind === "block" && <CalendarBlockDetailModal
        block={selectedEvent.source} organizationRef={selectedEvent.source.organizationRef || organizationRef} timezoneName={overviewZone}
        onHide={() => setSelectedEvent(null)} />}
      {supplier && <SupplierAvailabilityModal supplier={supplier} organizationRef={organizationRef} organizationName={orgContext.organizationName}
        name={practitionerNames[supplier.practitionerRef]} onHide={() => setSupplier(null)} />}
    </section>
  );
};

export default ScheduleOverview;
